import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Plus,
  Minus,
  ShoppingCart,
  Trash2,
  CreditCard,
  Gift,
  Edit3,
  Info,
} from "lucide-react";
import { CartItem } from "@/types";
import { SuggestedItems } from "./SuggestedItems";
import { getMenuItemImageUrl } from "@/services/utlits";
import { useApp } from "@/App";
import { useEffect, useState } from "react";
import apiConfig from "@/configs/apiConfig";

interface OrderSummaryProps {
  cartItems: CartItem[];
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onAddToCart: (item: CartItem) => void;
  orderMode?: { type: "pickup" | "delivery" };
}

export function OrderSummary({
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onAddToCart,
  orderMode,
}: OrderSummaryProps) {
  const { restaurant } = useApp();
  const [openTaxToolTip, setOpenTaxToolTip] = useState(false);
  const subtotal = cartItems.reduce(
    (sum, item) => sum + (item.totalPrice || item.basePrice) * item.quantity,
    0,
  );
  const deliveryFee = orderMode?.type === "pickup" ? 0 : apiConfig.DELIVERY_FEE;
  // const deliveryFee =
  //   orderMode?.type === "pickup"
  //     ? 0
  //     : subtotal > 35
  //     ? 0
  //     : apiConfig.DELIVERY_FEE;
  const [tax, setTax] = useState(0);
  const [serviceFee, setServiceFee] = useState(0);
  const total = subtotal + deliveryFee + tax;
  const recentlyAdded = cartItems[cartItems.length - 1];
  useEffect(() => {
    if (restaurant?.taxPercentage != null && !isNaN(subtotal)) {
      const calculatedTax = (subtotal * restaurant.taxPercentage) / 100;
      setTax(calculatedTax);
    } else {
      setTax(0);
    }
    if (restaurant?.serviceFee) {
      setServiceFee(restaurant.serviceFee);
    }
  }, [restaurant, subtotal]);
  if (cartItems.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="sticky top-28 shadow-soft border border-gray-100 bg-white">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-t-xl">
            <CardTitle className="flex items-center space-x-3 text-emerald-800">
              <div className="w-7 h-7 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-4 h-4 text-white" />
              </div>
              <span className="font-display text-base">Your Order</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-center py-8 text-gray-500">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-base font-display">
                Your cart is empty
              </h3>
              <p className="text-sm text-gray-600">
                Add some delicious Indian dishes to get started!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="sticky top-28 shadow-soft border border-gray-100 bg-white">
        <CardHeader className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-t-xl">
          <CardTitle className="flex items-center justify-between text-emerald-800">
            <div className="flex items-center space-x-3">
              <div className="w-7 h-7 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-4 h-4 text-white" />
              </div>
              <span className="font-display text-base">Your Order</span>
            </div>
            <Badge className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-medium text-xs px-2 py-1">
              {cartItems.reduce((sum, item) => sum + item.quantity, 0)} items
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          {/* Cart Items */}
          <div className="space-y-3 max-h-72 overflow-y-auto custom-scrollbar">
            {cartItems.map((item, index) => (
              <div
                key={`${item.id}-${index}`}
                className="p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-100"
              >
                <div className="flex items-start space-x-3">
                  <img
                    src={getMenuItemImageUrl(item.imageUrl)}
                    alt={item.name}
                    className="w-12 h-12 object-cover rounded-lg shadow-sm flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-gray-900 mb-1 line-clamp-1 font-display">
                      {item.name}
                    </h4>

                    {/* Customizations */}
                    {item.customizations && item.customizations.length > 0 && (
                      <div className="space-y-0.5 mb-2">
                        {item.customizations
                          .slice(0, 2)
                          .map((customization, idx) => (
                            <div
                              key={idx}
                              className="text-xs text-gray-600 flex items-center justify-between"
                            >
                              <span className="line-clamp-1">
                                {customization.optionName}
                              </span>
                              {customization.price > 0 && (
                                <span className="text-emerald-600 font-medium">
                                  +${customization.price.toFixed(2)}
                                </span>
                              )}
                            </div>
                          ))}
                        {item.customizations.length > 2 && (
                          <div className="text-xs text-gray-500">
                            +{item.customizations.length - 2} more
                          </div>
                        )}
                      </div>
                    )}

                    {/* Special Instructions */}
                    {item.specialInstructions && (
                      <div className="mb-2">
                        <div className="text-xs text-gray-600 flex items-start space-x-1">
                          <Edit3 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-1">
                            {item.specialInstructions}
                          </span>
                        </div>
                      </div>
                    )}

                    <p className="text-sm text-emerald-600 font-bold mb-2">
                      $
                      {(
                        (item.totalPrice || item?.basePrice) * item.quantity
                      ).toFixed(2)}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            onUpdateQuantity(item.id, item.quantity - 1)
                          }
                          disabled={item.quantity <= 1}
                          className="h-6 w-6 p-0 border-gray-300 hover:border-emerald-500 hover:bg-emerald-50"
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-6 text-center text-sm font-bold">
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            onUpdateQuantity(item.id, item.quantity + 1)
                          }
                          className="h-6 w-6 p-0 border-gray-300 hover:border-emerald-500 hover:bg-emerald-50"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveItem(item.id)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Separator />

          {/* Order Totals */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-semibold">${subtotal.toFixed(2)}</span>
            </div>
            {orderMode?.type === "delivery" && deliveryFee >= 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Delivery Fee</span>
                {deliveryFee ? (
                  <span className="font-semibold">
                    ${deliveryFee.toFixed(2)}
                  </span>
                ) : (
                  <span className="text-[12px] text-gray-400 italic text-right w-full block">
                    Delivery charges will be additional
                  </span>
                )}
              </div>
            )}
            <div className="flex justify-between text-sm items-center">
              <div className="flex items-center gap-1 relative">
                <span className="text-gray-600">Taxes and Service fee</span>
                <button
                  type="button"
                  onClick={() => setOpenTaxToolTip(!openTaxToolTip)}
                  onMouseEnter={() => setOpenTaxToolTip(true)}
                  onMouseLeave={() => setOpenTaxToolTip(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Info size={18} />
                </button>
                {/* Tooltip */}
                {openTaxToolTip && (
                  <div className="absolute top-6 left-0 z-50 w-52 rounded-md border bg-white p-3 shadow-lg text-xs">
                    {restaurant?.taxRules?.map((tax: any) => (
                      <div key={tax.name} className="flex justify-between">
                        <span>
                          {tax.name} ({tax.percentage}%)
                        </span>
                        <span>
                          ${((subtotal * tax.percentage) / 100).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between">
                      <span>Service fee</span>
                      <span>${restaurant?.serviceFee?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
              {restaurant && (
                <span className="font-semibold">
                  ${(tax + restaurant.serviceFee)?.toFixed(2)}
                </span>
              )}
            </div>
            <Separator />
            <div className="flex justify-between text-base font-bold">
              <span>Total</span>
              <span className="text-emerald-600">
                ${(total + serviceFee).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Checkout Button */}
          <Link to="/checkout">
            <Button
              className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold h-12 text-base rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
              size="lg"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Proceed to Checkout
            </Button>
          </Link>

          {/* Order Info */}
          <div className="text-xs text-gray-500 text-center space-y-1 pt-3 border-t border-gray-100">
            <p className="font-medium">🚚 Estimated delivery: 25-35 minutes</p>
            <p>📧 Order confirmation via email</p>
            {/* {subtotal < 35 && (
              <p className="text-emerald-600 font-medium bg-emerald-50 p-2 rounded-lg text-xs">
                💡 Add ${(35 - subtotal).toFixed(2)} more for free delivery!
              </p>
            )} */}
          </div>
        </CardContent>
      </Card>

      {/* Suggested Items */}
      <SuggestedItems recentlyAdded={recentlyAdded} onAddToCart={onAddToCart} />
    </div>
  );
}
